import { useCallback, useEffect, useMemo, useState } from 'react';
import { teacherService } from '@/school-management/services/teacher';
import type { TeacherReportItem } from '@/school-management/types/response';
import type { MultiSelectOption } from '@/components/school-management/MultiSelectDropDown/MultiSelectDropDown';

type UseCoTeacherSelectArgs = {
  organizationId: string;
  currentTeacherId?: string;
  preselectedTeacherIds?: string[];
  enabled?: boolean;
};

export function useCoTeacherSelect({
  organizationId,
  currentTeacherId,
  preselectedTeacherIds = [],
  enabled = true,
}: UseCoTeacherSelectArgs) {
  const [firstPageTeachers, setFirstPageTeachers] = useState<TeacherReportItem[]>([]);
  const [additionalTeachers, setAdditionalTeachers] = useState<TeacherReportItem[]>([]);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([1]));
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(true);

  useEffect(() => {
    if (!enabled || !organizationId) {
      setIsLoadingFirstPage(false);
      return;
    }

    const fetchFirstPage = async () => {
      setIsLoadingFirstPage(true);
      try {
        const response = await teacherService.getAllTeachers(organizationId, { page: 1 });
        setTotalPages(response.data?.totalPages ?? 1);
        setFirstPageTeachers(response.data?.items ?? []);
        setLoadedPages(new Set([1]));
        setAdditionalTeachers([]);
      } catch (error) {
        console.error('Error fetching teachers page info:', error);
        setFirstPageTeachers([]);
      } finally {
        setIsLoadingFirstPage(false);
      }
    };

    void fetchFirstPage();
  }, [organizationId, enabled]);

  useEffect(() => {
    if (!enabled || !organizationId || firstPageTeachers.length === 0) {
      return;
    }

    const missingTeacherIds = preselectedTeacherIds.filter(
      teacherId =>
        teacherId !== currentTeacherId && !firstPageTeachers.some(t => t.userId === teacherId)
    );

    if (missingTeacherIds.length === 0) {
      return;
    }

    const fetchSelectedTeachers = async () => {
      try {
        const results = await Promise.all(
          missingTeacherIds.map(async teacherId => {
            const response = await teacherService.getAllTeachers(organizationId, { teacherId });
            return response.data?.items?.find(t => t.userId === teacherId) ?? null;
          })
        );
        const fetchedTeachers = results.filter((t): t is TeacherReportItem => t != null);
        if (fetchedTeachers.length > 0) {
          setAdditionalTeachers(prev => {
            const existingIds = new Set([
              ...prev.map(t => t.userId),
              ...firstPageTeachers.map(t => t.userId),
            ]);
            return [...prev, ...fetchedTeachers.filter(t => !existingIds.has(t.userId))];
          });
        }
      } catch (error) {
        console.error('Error fetching selected co-teachers:', error);
      }
    };

    void fetchSelectedTeachers();
  }, [enabled, organizationId, firstPageTeachers, preselectedTeacherIds, currentTeacherId]);

  const handleLoadMore = useCallback(async () => {
    if (!organizationId || isLoadingMore) {
      return;
    }

    let nextPage = 1;
    for (let i = 2; i <= totalPages; i++) {
      if (!loadedPages.has(i)) {
        nextPage = i;
        break;
      }
    }

    if (nextPage === 1 || nextPage > totalPages) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const response = await teacherService.getAllTeachers(organizationId, { page: nextPage });
      if (response.data?.items) {
        setAdditionalTeachers(prev => {
          const existingIds = new Set([
            ...prev.map(t => t.userId),
            ...firstPageTeachers.map(t => t.userId),
          ]);
          const newTeachers = response.data.items.filter(
            (t: TeacherReportItem) => !existingIds.has(t.userId)
          );
          return [...prev, ...newTeachers];
        });
        setLoadedPages(prev => new Set([...prev, nextPage]));
      }
    } catch (error) {
      console.error('Error loading more teachers:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [organizationId, loadedPages, totalPages, isLoadingMore, firstPageTeachers]);

  const coTeacherOptions: MultiSelectOption[] = useMemo(() => {
    const teachersMap = new Map<string, TeacherReportItem>();

    firstPageTeachers.forEach(teacher => {
      teachersMap.set(teacher.userId, teacher);
    });

    additionalTeachers.forEach(teacher => {
      teachersMap.set(teacher.userId, teacher);
    });

    return Array.from(teachersMap.values())
      .filter(teacher => teacher.userId !== currentTeacherId)
      .map(teacher => ({
        label: `${teacher.firstName} ${teacher.lastName}`,
        value: teacher.userId,
        data: { email: teacher.email },
      }));
  }, [firstPageTeachers, additionalTeachers, currentTeacherId]);

  const hasMoreTeachers = loadedPages.size < totalPages;

  return {
    coTeacherOptions,
    handleLoadMore,
    hasMoreTeachers,
    isLoadingMore,
    isLoadingFirstPage,
  };
}
